import { hiddenModeRefusal } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The installments line under the cart's total — the "up to 6x $16,66" the
 * theme renders in the cart drawer and on the cart page.
 *
 * The third installments target, next to `product-detail-installments` and
 * `product-grid-installments`, and independent of both: the cart line is a
 * different element, so hiding it touches neither the product detail's panel
 * nor the cards.
 *
 * ## The selector
 *
 * `.js-installments-cart-total` is what the cart branch of the shared
 * `our/components/installments.tpl` emits, and what the local copies of that
 * component in atlantico, bahia, cubo, ipanema and zenit-flex emit as well. It
 * is never emitted by the product branches, so no ancestor scope is needed.
 *
 * It is rendered by 9 of the 24 SDK-carrying themes: atlantico, bahia, baires,
 * cali, cubo, guadalajara, ipanema, uyuni and zenit-flex. The other 15 show no
 * installments in the cart at all, so the target answers `not_found` there,
 * which is the truth rather than a gap. Every one of the 9 renders it from
 * `cart-totals.tpl`, which is shared by the cart page and the cart drawer, so
 * both match. All of them skip it when `settings.payment_discount_price` is
 * on, which shows the payment discount in its place.
 *
 * ## Durability
 *
 * The platform's cart script (`refreshTotalInstallments` in
 * `stores/resources/files/entry*.js`) rewrites the amounts inside this element
 * on every cart change and calls jQuery `.show()` / `.hide()` on it — an inline
 * `display`, which is exactly what `../visibility.ts` outranks with
 * `!important`. The element itself is never recreated, so the class survives
 * the shopper adding, removing or changing the quantity of an item.
 *
 * Readable with `get`, which reports the SDK's own mode, as `read` does.
 *
 * Only `hidden` for now: the wording inside is the theme's own phrasing of a
 * total the store computes, and the script above rewrites it on every cart
 * change, so `text` is not offered.
 *
 * @see ./README.md
 */
export const cartInstallments: CustomizationHandler<"cart-installments"> = {
	host: "storefront",
	selector: ".js-installments-cart-total",

	read(element) {
		// The themes render this line with an inline `display: none` when the
		// cart has no installments to show, and the cart script toggles it from
		// then on. Neither is part of the snapshot: the SDK never writes
		// `display` here, so it has nothing of the theme's to give back. See
		// ../visibility.ts.
		return { hidden: readHidden(element) };
	},

	operation(element, options) {
		if (options.hidden !== undefined) writeHidden(element, options.hidden);
	},

	values(element) {
		// The SDK's own mode, like `read`: the cart script's `display` is not
		// ours to report, see ../visibility.ts.
		return { hidden: readHidden(element) };
	},

	validate(_element, options) {
		if (options.hidden !== undefined) {
			const refusal = hiddenModeRefusal(options.hidden);
			if (refusal) return `refused "hidden": ${refusal}`;
		}

		return null;
	},
};
